import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { requestVisitorCode, verifyVisitorCode } from "../api.js";
import { AskForAccess } from "../components/AskForAccess.js";
import { Lockup } from "../components/Logo.js";

/**
 * Confirming an invitation (1d, 1e).
 *
 * The screen deliberately cannot tell a visitor whether the link was real: a
 * code is "sent" either way, and only entering the right one proves anything.
 * That is the same rule the server follows, and it is what stops this page
 * becoming a way to ask whether somebody uses Meet With.
 *
 * Which leaves a person who genuinely cannot get in - a forwarded link (2u),
 * or an invitation since revoked (2t) - looking at "that code wasn't right"
 * with nowhere to go. So after a couple of failures the recovery appears. It
 * still says nothing about why; it just stops being a dead end.
 */
export function VisitorConfirm() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(false);

  const send = useMutation({
    mutationFn: () => requestVisitorCode(token),
    onSuccess: () => setSent(true),
  });

  const [failures, setFailures] = useState(0);
  const verify = useMutation({
    mutationFn: () => verifyVisitorCode(token, code, name || undefined),
    onSuccess: () => navigate("/book", { replace: true }),
    onError: () => setFailures((count) => count + 1),
  });

  // Two wrong codes is a typo; a third is usually the wrong person holding the
  // link, or an invitation that no longer works.
  const stuck = failures >= 2;

  // Requested once on arrival, keyed on the token; resending is explicit.
  const [requestedFor, setRequestedFor] = useState<string | null>(null);
  useEffect(() => {
    if (token && requestedFor !== token) {
      setRequestedFor(token);
      send.mutate();
    }
  }, [token, requestedFor, send]);

  return (
    <main className="centered">
      <section className="card" aria-labelledby="confirm-heading">
        <Lockup size={22} />
        <h1 id="confirm-heading">Confirm it&rsquo;s you</h1>
        <p className="lede">
          We&rsquo;ve sent a 6-digit code to the address this invitation was sent to.
          Enter it to see when your host is free.
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            verify.mutate();
          }}
        >
          <label className="field">
            <span>Code</span>
            <input
              className="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            />
          </label>

          <label className="field">
            <span>Your name</span>
            <input
              type="text"
              placeholder="Priya Natarajan"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <span className="helper">So your host knows who&rsquo;s asking.</span>
          </label>

          {verify.isError ? (
            <div className="notice notice-error" role="alert">
              <strong>That didn&rsquo;t work</strong>
              <span>{verify.error.message}</span>
            </div>
          ) : null}

          <div className="row">
            <button type="submit" className="primary" disabled={verify.isPending}>
              {verify.isPending ? "Checking…" : "Continue"}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => send.mutate()}
              disabled={send.isPending}
            >
              {sent ? "Send a new code" : "Send the code"}
            </button>
          </div>
        </form>

        <p className="footnote">
          No account, password or calendar connection is needed. Your email is only used
          to check the invitation.
        </p>

        {stuck ? (
          <div className="recovery">
            <h2>Not getting the code?</h2>
            <p className="helper">
              The code only ever goes to the address the invitation was sent to. If
              this link reached you from someone else, or your access has ended, it
              won&rsquo;t arrive here — and we can&rsquo;t say which. Ask for an
              invitation of your own instead.
            </p>
            <AskForAccess lead="Whoever you were trying to reach decides; we only pass it along." />
          </div>
        ) : null}
      </section>
    </main>
  );
}
